/** One site's page — Batch 5, `docs/design/36-user-journeys.md`.
 *
 * > *"Take me from any figure to its source. Show me nothing else."*
 *
 * ## Every fact here is borrowed, and the page keeps no second copy of any
 *
 * The site list (`useFacilities`), the census (`useCoverage`) and the meters (`useMeters`) each have
 * an owner, and this page **filters their answers to one site** rather than asking the server a
 * differently-shaped question. That is `SupplierAccount`'s rule and the record page's: a second copy
 * of a name or a total eventually disagrees with the first.
 *
 * ## No number here is added up in the browser
 *
 * The page used to load every figure in the window (`useFigures`) and filter and sum them to one
 * site, which stopped working past the client's load ceiling. Now the **scope tiles and bars are
 * the server's**: `GET /inventory/monthly?facility_id=` carries this site's total, count and
 * "assessed" per scope, folded by the same rule as the inventory (both of ADR-0030's exclusions,
 * ADR-0029's allocation). The **Figures tab is a page of the register**, `GET /figures` narrowed to
 * the site on the server (`useFiguresPaged`), saying *Showing N of M* and reading the next page when
 * asked. The list is a transport for rows to open; it is never summed.
 *
 * ## The month-by-month chart is the server's, narrowed to the site
 *
 * `GET /inventory/monthly?facility_id=` runs the workspace series' own fold over this site's
 * calculations only (ADR-0110: a month is the difference of two cumulative allocations, so the
 * months add up to the window total exactly). The page draws the same `MonthlyChart` the Emissions
 * screen draws, and checks the months against this site's entry in `GET /inventory`'s `by_facility`
 * - the number the rest of the product prints for the site - and refuses to draw a series that does
 * not reconcile. It is not built from the figures' periods in the browser.
 *
 * ## What is not drawn, and why it is said in words instead
 *
 * - **No Scope 3 attributed to a site.** A site carries the figures its own calculations produced.
 *   A scope with none reads *None recorded*, never `0.0`: unknown is not zero.
 * - **No operational start or end date from the site record.** The API serves neither on a site. A
 *   closure shows only where it ends a source's life (`life_to_basis = site`), and the page says that
 *   is where the date came from.
 * - **No per-site ledger filter.** The chain has none by design (`api/ledger.ts`: a filter is how a
 *   chain becomes a list that cannot be verified). The History tab searches the newest page only
 *   and says so.
 *
 * ## No write controls here
 *
 * Correcting a site, answering its lease treatment and retiring it stay on Organisation, and the
 * lease history dialog is that screen's own component. A second path to the same write is how two
 * screens come to disagree; this page links back.
 */

import { Buildings } from "@phosphor-icons/react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../api/auth";
import { useFacilities } from "../api/evidence";
import { useReader } from "../api/profile";
import { useMeters } from "../api/provisioning";
import { useFiguresPaged, useInventory, useInventoryMonthly } from "../api/queries";
import type { FigureCutRead } from "../api/queries";
import { useChain } from "../api/ledger";
import { useCoverage } from "../api/sources";
import type { SourceCoverage } from "../api/sources";
import type { FacilityRow } from "../api/evidence";
import { Bars } from "../components/Bars";
import { Basis } from "../components/Basis";
import { Loaded, ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { PageHeaderCard, Tile, TileStrip } from "../components/ui";
import { StatusPill } from "../components/StatusPill";
import type { StatusTone } from "../components/StatusPill";
import { instantStamp, quantity, shortDate, shortHash, statedQuantity, tonnes, unitLabel } from "../lib/format";
import type { InfoKey } from "../shell/infoText";
import { RequireSession } from "../shell/RequireSession";
import { LeaseCorrections } from "./LeaseCorrections";
import { LEASE_LABEL } from "./organisationOptions";
import { RecordPanel, RecordTabs } from "./RecordTabs";
import { ScreenFrame } from "./ScreenFrame";
import { MonthlyChart } from "./InventoryMonthly";
import { modelOf } from "./monthlyModel";
import { keepSearch } from "./search";
import { LIFE_BASIS, countStates, nameOf, siteClosure } from "./sourceModel";
import { StateTally } from "./StateTally";
import { useScreen } from "./useScreen";
import type { Screen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";
import styles from "./DetailPage.module.css";

const SITE_TABS = [
  { key: "overview", label: "Overview" },
  { key: "sources", label: "Sources" },
  { key: "meters", label: "Meters" },
  { key: "figures", label: "Figures" },
  { key: "history", label: "History" },
] as const;

/** Mirrored from `api/ledger.py`: the chain is read by these three roles. */
const MAY_READ_LEDGER: readonly string[] = ["admin", "approver", "auditor"];

/** How many figure rows are drawn before the reader asks for more. */
const FIGURE_CEILING = 40;

const SCOPE_COLOUR: Record<string, string> = { "1": "var(--s1)", "2": "var(--s2)", "3": "var(--s3)" };

function sentenceCase(text: string): string {
  const spaced = text.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Who operates the site, in words. `null` is *not stated*, which is a fact about what was
 * recorded and not a "no". */
function operatesWords(operates: boolean | null | undefined): string {
  if (operates === true) return "The company operates it";
  if (operates === false) return "Somebody else operates it";
  return "Who operates it is not stated";
}

function SiteStatus({
  site,
  sources,
}: {
  site: FacilityRow;
  sources: readonly SourceCoverage[] | undefined;
}) {
  const closure = sources ? siteClosure(sources) : null;
  const leaseTone: StatusTone = site.lease_type === "unknown" ? "warn" : "ok";
  return (
    <div className={styles.statusRow} data-testid="site-status">
      <StatusPill tone={leaseTone} testId="site-lease-pill">
        {site.lease_type === "unknown"
          ? "Lease treatment not answered"
          : `Lease treatment: ${LEASE_LABEL[site.lease_type] ?? site.lease_type}`}
      </StatusPill>
      <StatusPill tone="neutral">{operatesWords(site.operates)}</StatusPill>
      {closure ? (
        <StatusPill tone="warn" testId="site-life-pill">
          Closed {shortDate(closure)}
        </StatusPill>
      ) : (
        <StatusPill tone="neutral" testId="site-life-pill">
          No closure date recorded
        </StatusPill>
      )}
    </div>
  );
}

interface SiteTile {
  key: string;
  label: string;
  value: string;
  unit?: string;
  note?: string;
  infoKey?: InfoKey;
}

/** The four tiles of the shared tile component. A figure is a number or a dash, never a zero: a read that
 * failed leaves a dash and says so beside the strip. */
function SiteFacts({
  series,
  census,
  method,
  facilityId,
}: {
  series: ReturnType<typeof useInventoryMonthly>;
  census: ReturnType<typeof useCoverage>;
  method: string;
  facilityId: string;
}) {
  // Four tiles, always: a figure that could not be read is a dash, never a zero (the read that failed says so
  // above them). What each one rests on is a short line under the figure, not a sentence.
  const tiles: SiteTile[] = [];
  for (const scope of ["1", "2", "3"] as const) {
    // This site's total and count for the scope, as the server summed them.
    const card = series.data?.totals_by_scope.find((entry) => String(entry.scope) === scope);
    const recorded = card !== undefined && card.assessed && card.figures > 0;
    tiles.push({
      key: `site-scope-${scope}`,
      label: scope === "2" ? `Scope 2, ${method}-based` : `Scope ${scope}`,
      value: !series.data ? "—" : recorded ? tonnes(card.kgco2e) : "None recorded",
      unit: series.data && recorded ? "tCO₂e" : undefined,
      note: !series.data
        ? undefined
        : recorded
          ? `${card.figures} figure${card.figures === 1 ? "" : "s"}`
          : scope === "3"
            ? "Not attributed to a site"
            : "No figure this window",
    });
  }
  {
    const mine = census.data?.sources.filter((source) => source.facility_id === facilityId);
    const counts = mine ? countStates(mine.flatMap((source) => source.windows)) : undefined;
    const arrived = counts ? (counts.submitted ?? 0) + (counts.declared_nil ?? 0) : 0;
    const due = counts ? arrived + (counts.missing ?? 0) : 0;
    tiles.push({
      key: "site-sources",
      label: "Periods accounted for",
      value: !mine ? "—" : mine.length === 0 ? "None expected" : `${arrived} of ${due}`,
      unit: mine && mine.length > 0 ? "windows" : undefined,
      note: !mine
        ? undefined
        : mine.length === 0
          ? "Nothing expected yet"
          : `${mine.length} expected source${mine.length === 1 ? "" : "s"}`,
      infoKey: "facilities.site-windows",
    });
  }
  return (
    <>
      {series.error ? (
        <div className={styles.band}>
          <ReadFailed
            of="This site's figures"
            error={series.error}
            onRetry={() => void series.refetch()}
          />
        </div>
      ) : null}
      {census.error ? (
        <div className={styles.band}>
          <ReadFailed
            of="This site's expected bills"
            error={census.error}
            onRetry={() => void census.refetch()}
          />
        </div>
      ) : null}
      <div className={styles.band}>
        <TileStrip label="This site, this window">
          {tiles.map((tile) => (
            <Tile
              key={tile.key}
              kpi={tile.key}
              label={tile.label}
              value={tile.value}
              unit={tile.unit}
              infoKey={tile.infoKey}
              link={tile.note ? <span className={styles.tileNote}>{tile.note}</span> : undefined}
            />
          ))}
        </TileStrip>
      </div>
    </>
  );
}

function OverviewTab({
  site,
  series,
  closure,
  onOpenFigures,
  monthly,
  method,
  onOpenMonth,
}: {
  site: FacilityRow;
  /** This site's series: its per-scope totals are the bars, and its months are the chart. */
  series: ReturnType<typeof useInventoryMonthly>;
  closure: string | null;
  /** A scope's total is a sum, so it opens the figures it is made of (the receipt rule). */
  onOpenFigures: () => void;
  /** This site's months, and the total they must add up to; both undefined without a window. */
  monthly: { read: ReturnType<typeof useInventoryMonthly>; expectedKg: string | undefined } | undefined;
  method: "location" | "market";
  /** Open the register for the site and one month's dates. */
  onOpenMonth: (bucketStart: string, bucketEnd: string) => void;
}) {
  const bars = (["1", "2", "3"] as const)
    .map((scope) => {
      const card = series.data?.totals_by_scope.find((entry) => String(entry.scope) === scope);
      return { scope, kg: card && card.assessed && card.figures > 0 ? card.kgco2e : undefined };
    })
    .filter((entry): entry is { scope: "1" | "2" | "3"; kg: string } => entry.kg !== undefined);
  const largest = Math.max(0, ...bars.map((entry) => Number(entry.kg)));
  return (
    <>
      <Panel title="This site" sub="As recorded on the Organisation screen" column="1 / 13">
        <dl className={styles.kv} data-testid="site-facts">
          <dt>Company</dt>
          <dd>{site.legal_entity_name}</dd>
          <dt>Type</dt>
          <dd>{sentenceCase(site.facility_type)}</dd>
          <dt>Jurisdiction</dt>
          <dd>
            {site.jurisdiction}
            {site.country ? ` · ${site.country}` : ""}
            {site.grid_region ? ` · grid region ${site.grid_region}` : ""}
          </dd>
          <dt>Lease or control basis</dt>
          <dd>
            {LEASE_LABEL[site.lease_type] ?? site.lease_type}. {operatesWords(site.operates)}.
          </dd>
          <dt>Operational life</dt>
          <dd>
            {closure
              ? `Closed ${shortDate(closure)}, as the expected-bills check finds it from its sources.`
              : "No closure date recorded. The site record carries no start or end date; a closure appears only where it ends a source's life."}
          </dd>
        </dl>
      </Panel>
      <Panel title="Emissions by scope" sub="tCO₂e from this site's figures in the window in force" column="1 / 13">
        {series.error ? (
          <ReadFailed
            of="This site's figures"
            error={series.error}
            onRetry={() => void series.refetch()}
          />
        ) : series.isPending ? (
          <Empty>Loading this site&rsquo;s figures…</Empty>
        ) : bars.length === 0 ? (
          <Empty>No figure for this site in this window. Add a bill or a reading and it appears here.</Empty>
        ) : (
          <Bars
            caption="tCO₂e by scope for this site"
            rows={bars.map((entry) => ({
              id: `scope-${entry.scope}`,
              name: `Scope ${entry.scope}`,
              value: tonnes(entry.kg),
              fraction: largest > 0 ? Number(entry.kg) / largest : 0,
              colour: SCOPE_COLOUR[entry.scope] ?? "var(--s1)",
              onOpen: onOpenFigures,
            }))}
          />
        )}
      </Panel>
      {monthly ? (
        <div data-testid="site-monthly">
          <MonthlyChart
            read={monthly.read}
            model={monthly.read.data ? modelOf(monthly.read.data, monthly.expectedKg) : undefined}
            method={method}
            onOpenMonth={onOpenMonth}
          />
          <p className={styles.note} data-testid="site-monthly-note">
            Here a scope marked &ldquo;not assessed&rdquo; has no figure for this site in the window.
            Scope 3 is assessed for the company and is not attributed to a site. Opening a month shows
            this site&rsquo;s register for those dates.
          </p>
        </div>
      ) : null}
      <Basis
        summary="What this page does not show, and why"
        sub="Basis and limits"
        testId="site-basis"
      >
        <p className={styles.note}>
          The months are this site&rsquo;s own calculations allocated by the same rule as the
          workspace&rsquo;s chart, and are checked against this site&rsquo;s total in the inventory
          before they are drawn. A scope with no figure reads
          &ldquo;None recorded&rdquo;, which is not a zero. Scope 3 is assessed for the company, not
          attributed to a site. Each total is the sum of the figures on the Figures tab, and each of
          those opens its receipt.
        </p>
      </Basis>
    </>
  );
}

function SourcesTab({
  site,
  census,
}: {
  site: FacilityRow;
  census: ReturnType<typeof useCoverage>;
}) {
  return (
    <Panel
      title="Expected sources"
      sub={`What ${site.name} is expected to send, and where each period stands`}
      column="1 / 13"
    >
      <Loaded
        query={census}
        of="This site's expected bills"
        empty={<Empty>No sources have been declared.</Empty>}
      >
        {(data) => {
          const mine = data.sources.filter((source) => source.facility_id === site.id);
          if (mine.length === 0) {
            return (
              <Empty>
                Nothing is expected from this site, so completeness here is <strong>unknown</strong>{" "}
                and not complete. Declare an expected source on the Sources screen.
              </Empty>
            );
          }
          return (
            <ul className={styles.list} data-testid="site-sources">
              {mine.map((source) => (
                <li key={source.source_id} className={styles.row} data-source-id={source.source_id}>
                  <span className={styles.rowMain}>
                    <Link
                      className={styles.link}
                      to="/sources/$sourceId"
                      params={{ sourceId: source.source_id }}
                      search={keepSearch}
                      data-testid="site-source-link"
                    >
                      {nameOf(source)}
                    </Link>
                    <span className={styles.rowMeta}>
                      {sentenceCase(source.category)} · {source.cadence.replace(/_/g, "-")}
                      {source.is_retired ? ` · retired ${shortDate(source.expected_to)}` : ""}
                      {source.life_to && source.life_to_basis !== "declaration"
                        ? ` · nothing expected after ${shortDate(source.life_to)} (${LIFE_BASIS[source.life_to_basis ?? ""]?.said ?? source.life_to_basis})`
                        : ""}
                    </span>
                    <StateTally windows={source.windows} />
                  </span>
                  <span className={styles.rowSide}>
                    {source.owner_user_id === null ? "No operating owner" : "Has an operating owner"}
                  </span>
                </li>
              ))}
            </ul>
          );
        }}
      </Loaded>
    </Panel>
  );
}

function MetersTab({
  site,
  census,
}: {
  site: FacilityRow;
  census: ReturnType<typeof useCoverage>;
}) {
  const meters = useMeters(site.id);
  const streams = census.data?.sources.filter((source) => source.facility_id === site.id) ?? [];
  return (
    <Panel title="Meters" sub="The supply points at this site" column="1 / 13">
      <Loaded
        query={meters}
        of="This site's meters"
        empty={<Empty>No meters are recorded for this site. Add one on the Organisation screen.</Empty>}
      >
        {(rows) => (
          <ul className={styles.list} data-testid="site-meters">
            {rows.map((meter) => {
              const used = streams.filter((source) => source.meter_id === meter.id);
              return (
                <li key={meter.id} className={styles.row} data-meter-id={meter.id}>
                  <span className={styles.rowMain}>
                    <span className={styles.rowName}>{meter.label ?? sentenceCase(meter.kind)}</span>
                    <span className={styles.rowMeta}>
                      {sentenceCase(meter.kind)} · {unitLabel(meter.unit)}
                      {meter.operational_from ? ` · in service from ${shortDate(meter.operational_from)}` : ""}
                    </span>
                    {used.length > 0 ? (
                      <span className={styles.rowMeta}>
                        Reports:{" "}
                        {used.map((source, index) => (
                          <span key={source.source_id}>
                            {index > 0 ? ", " : ""}
                            <Link
                              className={styles.link}
                              to="/sources/$sourceId"
                              params={{ sourceId: source.source_id }}
                              search={keepSearch}
                            >
                              {nameOf(source)}
                            </Link>
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </span>
                  <span className={styles.rowSide}>
                    {meter.operational_to ? (
                      <StatusPill tone="warn">Retired {shortDate(meter.operational_to)}</StatusPill>
                    ) : (
                      <StatusPill tone="ok">In service</StatusPill>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Loaded>
    </Panel>
  );
}

function FiguresTab({
  screen,
  figures,
  ceiling,
  setCeiling,
}: {
  screen: Screen;
  /** A page of the register narrowed to this site **by the server**, and the server's count of it. */
  figures: FigureCutRead;
  ceiling: number;
  setCeiling: (next: number) => void;
}) {
  const shown = figures.figures.slice(0, ceiling);
  const remaining = figures.total - shown.length;
  const listedAll = figures.ceilingReached && shown.length >= figures.figures.length;
  const showMore = () => {
    setCeiling(ceiling + FIGURE_CEILING);
    // Rows already loaded are drawn first; the next page is asked for only once they run out.
    if (ceiling + FIGURE_CEILING > figures.figures.length && figures.canLoadMore) figures.loadMore();
  };
  return (
    <Panel title="Figures" sub="Every figure this site's calculations produced in the window" column="1 / 13">
      {figures.error ? (
        <ReadFailed of="This site's figures" error={figures.error} onRetry={figures.refetch} />
      ) : figures.isPending ? (
        <Empty>Loading this site&rsquo;s figures…</Empty>
      ) : figures.total === 0 ? (
        <Empty>No figure for this site in this window. Add a bill or a reading and it appears here.</Empty>
      ) : (
        <>
          <p className={styles.toolbar} data-testid="site-figures-showing">
            Showing {shown.length} of {figures.total}
          </p>
          <ul className={styles.list} data-testid="site-figures">
            {shown.map((figure) => (
              <li key={figure.id} className={styles.row} data-figure-id={figure.id}>
                <span className={styles.rowMain}>
                  <span className={styles.rowName}>
                    {sentenceCase(figure.category)} · {sentenceCase(figure.fuel_or_carrier)}
                  </span>
                  <span className={styles.rowMeta}>
                    Scope {figure.scope} · {shortDate(figure.period_start)} –{" "}
                    {shortDate(figure.period_end)} · {statedQuantity(figure.quantity)} {unitLabel(figure.unit)} · grade{" "}
                    {figure.quality}
                  </span>
                </span>
                <span className={styles.rowSide}>
                  <span className={`${styles.value} num`}>{tonnes(figure.kgco2e)} tCO₂e</span>
                  <button
                    type="button"
                    className={styles.action}
                    data-testid="site-figure-receipt"
                    aria-label={`Receipt for ${sentenceCase(figure.category)}, ${shortDate(figure.period_start)} to ${shortDate(figure.period_end)}`}
                    onClick={() => screen.openReceipt(figure.id)}
                  >
                    Receipt
                  </button>
                  <Link
                    className={styles.link}
                    to="/records/$recordId"
                    params={{ recordId: figure.activity_record_id }}
                    search={keepSearch}
                    data-testid="site-figure-record"
                  >
                    Record ›
                  </Link>
                </span>
              </li>
            ))}
          </ul>
          {figures.moreError ? (
            <ReadFailed
              of="The next page of this site's figures"
              error={figures.moreError}
              onRetry={figures.loadMore}
            />
          ) : null}
          {remaining > 0 && !listedAll ? (
            <button
              type="button"
              className={styles.action}
              data-testid="site-figures-more"
              disabled={figures.loadingMore}
              onClick={showMore}
            >
              {figures.loadingMore ? "Loading…" : `Show ${Math.min(FIGURE_CEILING, remaining)} more`}
            </button>
          ) : null}
          {listedAll ? (
            <p className={styles.note} data-testid="site-figures-ceiling">
              {quantity(String(shown.length), 0)} of {quantity(String(figures.total), 0)} of this
              site&rsquo;s figures are listed here. The Emissions register filters them by month and category.
            </p>
          ) : null}
        </>
      )}
    </Panel>
  );
}

/** Ledger entries naming this site, **from the newest page only** (see the module comment). */
function LedgerAboutSite({ siteId, mayRead }: { siteId: string; mayRead: boolean }) {
  const reader = useReader();
  const chain = useChain(undefined, mayRead);
  if (!mayRead) {
    return (
      <p className={styles.note} data-testid="site-ledger-who">
        The ledger is read by an Admin, an Approver or an Auditor. Ask one of them to look for this
        site on the Ledger screen.
      </p>
    );
  }
  return (
    <Loaded query={chain} of="The newest ledger entries">
      {(page) => {
        const about = page.rows.filter(
          (entry) => entry.object_id === siteId || entry.payload.facility_id === siteId,
        );
        return (
          <>
            <p className={styles.note} data-testid="site-ledger-scope">
              Searched the newest {page.rows.length} entries only. The chain has no per-site filter
              (a filter would turn it into a list that cannot be verified), so older entries about this
              site are on the{" "}
              <Link className={styles.link} to="/ledger" search={keepSearch}>
                Ledger screen ›
              </Link>
            </p>
            {about.length === 0 ? (
              <Empty>None of the newest entries is about this site.</Empty>
            ) : (
              <ul className={styles.list} data-testid="site-ledger">
                {about.map((entry) => (
                  <li key={entry.seq} className={styles.row} data-seq={entry.seq}>
                    <span className={styles.rowMain}>
                      <span className={styles.rowName}>{entry.type}</span>
                      <span className={styles.rowMeta}>
                        #{entry.seq} · {instantStamp(entry.recorded_at, reader)} · {shortHash(entry.hash)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        );
      }}
    </Loaded>
  );
}

function HistoryTab({ site, isAdmin, mayReadLedger }: { site: FacilityRow; isAdmin: boolean; mayReadLedger: boolean }) {
  return (
    <>
      <Panel title="Lease treatment" sub="Which version of the answer is in force, and who gave it" column="1 / 13">
        <p className={styles.note} data-testid="site-lease-now">
          In force now: {LEASE_LABEL[site.lease_type] ?? site.lease_type}. {operatesWords(site.operates)}.
        </p>
        {isAdmin && site.lease_corrections_available ? (
          <LeaseCorrections facilityId={site.id} facilityName={site.name} />
        ) : (
          <p className={styles.note} data-testid="site-lease-who">
            {site.lease_corrections_available
              ? "An Admin reads the dated history of this answer and corrects it."
              : "The dated history of this answer is not available for this site yet. What is stated now is above."}
          </p>
        )}
      </Panel>
      <Panel title="In the ledger" sub="Entries that name this site" column="1 / 13">
        <LedgerAboutSite siteId={site.id} mayRead={mayReadLedger} />
      </Panel>
    </>
  );
}

function SiteBody({ site, screen }: { site: FacilityRow; screen: Screen }) {
  const me = useMe();
  const [tab, selectTab] = useUrlTab(SITE_TABS);
  const window = screen.window;
  //: This site's totals by scope and its months, summed by the server: the scope tiles, the bars and
  //: the chart all read this one query (same key, one request). It is read on every tab because the
  //: tiles above the tabs state it; no window, no request (`useInventory`).
  const series = useInventoryMonthly(window, screen.method, site.id);
  //: The Figures tab is a page of the register, narrowed to this site by the server. Read only when
  //: that tab is open.
  const figures = useFiguresPaged(tab === "figures" ? window : undefined, screen.method, {
    facilityIds: [site.id],
  });
  const census = useCoverage(window?.period_start, window?.period_end);
  const [ceiling, setCeiling] = useState(FIGURE_CEILING);
  const role = me.data?.role;
  const navigate = useNavigate();
  //: Read only for the Overview, and only with a window: no window, no request (`useInventory`).
  const onOverview = tab === "overview" ? window : undefined;
  const inventory = useInventory(onOverview, screen.method);
  //: The total this site's months must add up to: its own entry in the inventory's `by_facility`,
  //: which the rest of the product prints. A site with no entry has nothing allocated, which is
  //: zero *only once the inventory has been read* - until then there is nothing to check against.
  const expectedKg = inventory.data
    ? (inventory.data.by_facility.find((entry) => entry.facility_id === site.id)?.kgco2e ?? "0")
    : undefined;
  const mine = census.data?.sources.filter((source) => source.facility_id === site.id);
  const closure = mine ? siteClosure(mine) : null;

  return (
    <>
      <PageHeaderCard
        icon={Buildings}
        title={site.name}
        label={`${site.legal_entity_name} · ${site.jurisdiction}${site.grid_region ? ` · ${site.grid_region}` : ""}`}
        actions={
          <Link className={styles.link} to="/organisation" search={keepSearch}>
            Back to Organisation ›
          </Link>
        }
      />
      <SiteStatus site={site} sources={mine} />
      {window === undefined ? (
        <div className={styles.band}>
          <p className={styles.note} data-testid="site-no-window">
            This workspace has no reporting period, so this site&rsquo;s figures and its expected bills
            are not drawn. An Admin sets one up on the Organisation screen.
          </p>
        </div>
      ) : (
        <SiteFacts series={series} census={census} method={screen.method} facilityId={site.id} />
      )}
      <div className={styles.tabArea}>
        {/* The page's own tabs sit on the left under the header and the tiles, where every place's tabs are,
            and not at the right end of the header. */}
        <RecordTabs
          tabs={SITE_TABS}
          tab={tab}
          onSelect={selectTab}
          label="Site"
          saying={`Showing ${SITE_TABS.find((entry) => entry.key === tab)?.label ?? tab} for ${site.name}.`}
        />
        <RecordPanel tab={tab}>
          <div className={styles.stack}>
            {tab === "overview" ? (
              <OverviewTab
                site={site}
                series={series}
                closure={closure}
                onOpenFigures={() => selectTab("figures")}
                monthly={window ? { read: series, expectedKg } : undefined}
                method={screen.method}
                onOpenMonth={(from, to) =>
                  void navigate({
                    to: "/emissions",
                    search: { method: screen.method, tab: "register", site: site.id, from, to },
                  })
                }
              />
            ) : null}
            {tab === "sources" ? <SourcesTab site={site} census={census} /> : null}
            {tab === "meters" ? <MetersTab site={site} census={census} /> : null}
            {tab === "figures" ? (
              <FiguresTab screen={screen} figures={figures} ceiling={ceiling} setCeiling={setCeiling} />
            ) : null}
            {tab === "history" ? (
              <HistoryTab
                site={site}
                isAdmin={role === "admin"}
                mayReadLedger={role !== undefined && MAY_READ_LEDGER.includes(role)}
              />
            ) : null}
          </div>
        </RecordPanel>
      </div>
    </>
  );
}

function SiteDetailBody() {
  const screen = useScreen();
  const { facilityId } = useParams({ from: "/organisation/sites/$facilityId" });
  const sites = useFacilities();
  const site = sites.data?.find((entry) => entry.id === facilityId);
  return (
    <ScreenFrame screen={screen} title="Site" crumb={site?.name ?? "one site"} bodyScrolls>
      <Loaded
        query={sites}
        of="The sites in this workspace"
        empty={<NoSuchSite />}
      >
        {() => (site ? <SiteBody site={site} screen={screen} /> : <NoSuchSite />)}
      </Loaded>
    </ScreenFrame>
  );
}

/** The same sentence for a site that does not exist and one this session may not read: telling the
 * two apart would confirm that a site exists (`evidence._document`'s rule). */
function NoSuchSite() {
  return (
    <Panel title="Site" column="1 / 13">
      <p className={styles.note} data-testid="site-none">
        This workspace has no site with that address. Sites are listed on the Organisation screen.
      </p>
      <Link className={styles.link} to="/organisation" search={keepSearch}>
        Back to Organisation ›
      </Link>
    </Panel>
  );
}

export function SiteDetailScreen() {
  return (
    <RequireSession>
      <SiteDetailBody />
    </RequireSession>
  );
}
